import {AfterViewInit, Component, effect, ElementRef, HostListener, ViewChild} from '@angular/core';
import {NgOptimizedImage} from '@angular/common';
import {UserService} from '../../services/user/user.service';
import {InputDropdownComponent} from '../../widgets/input-dropdown/input-dropdown.component';
import {AuthService} from '../../services/auth/auth-service';
import {NavigationEnd, Router} from '@angular/router';
import {BrandingService} from '../../services/branding/branding-service';
import {BrandingConfig} from '../../models/branding.model';
import {MenuService} from '../../services/menu/menu-service';
import {ModalService} from '../../services/modal/modal-service';

@Component({
  selector: 'app-header-global',
  imports: [
    NgOptimizedImage,
    InputDropdownComponent
  ],
  templateUrl: './header-global.html',
  styleUrl: './header-global.scss'
})
export class HeaderGlobal implements AfterViewInit {
  imgLogo?: string;
  config: BrandingConfig | null = null;
  dittaSelected?: string;
  isLeftMenuOpen = false;

  /**
   * Flag usato per distinguere la chiusura "indiretta" del dropdown
   * (quando viene chiuso da codice aprendo il menu laterale) dalle
   * chiusure "dirette" generate dall'utente. Serve ad evitare che
   * l'handler onDropdownExpandedChange richiuda subito il menu appena
   * aperto.
   */
  private suppressDropdownCloseMenu: boolean = false;

  // Riferimento al dropdown ditta presente nell'header, usato
  // per chiuderlo quando si apre il menu laterale.
  @ViewChild(InputDropdownComponent) dittaDropdown?: InputDropdownComponent;

  constructor(
    private readonly menuService: MenuService,
    private readonly brandingService: BrandingService,
    private readonly authService: AuthService,
    protected readonly userService: UserService,
    private readonly router: Router,
    private readonly hostElement: ElementRef<HTMLElement>,
    private readonly modalService: ModalService
  ) {
    // Effetto reattivo: carica/svuota le info utente al variare dell'autenticazione
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.userService.fetchUserInfo();
      }
      else {
        // Stato non autenticato: pulizia e redirect a login
        this.logout();
      }
    });

    // Branding: aggiorna il logo in header in base all'host
    this.brandingService.config$.subscribe(cfg => {
      this.config = cfg;
      if (cfg?.logo) {
        this.imgLogo = cfg.logo;
      }
      else {
        this.imgLogo = undefined;
      }
    });

    // Alla fine di ogni navigazione, assicurati che il menu sinistro
    // risulti chiuso sia nel DOM che nello stato (isLeftMenuOpen).
    // Questo mantiene coerente l'icona dell'hamburger anche quando
    // la navigazione è stata innescata da una voce del menu laterale.
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.closeLeftMenuIfOpen();
      }
    });
  }

  /**
   * TODO quando ci sarà l'impersonifica si dovrò fare qualcosa (da definire)
   */
  onDittaSelected(dittaSelected: string): void {
    // Placeholder per futura logica di selezione ditta
    this.dittaSelected = dittaSelected;
    this.goHome(true);
  }

  /**
   * TODO quando ci sarà l'impersonifica si dovrà eliminare l'impersonificazione
   * e riportare tutto al commercialista che c'era prima
   */
  goHome(isDittaSelected: boolean = false): void {
    void this.router.navigate(['home']).then(() => {
      if (!isDittaSelected) this.dittaSelected = undefined;
      this.menuService.collapseAll();
    });
  }

  goAssistenza(): void {
    void this.router.navigate(['assistenza']).then(() => {
      this.menuService.collapseAll();
    });
  }

  logout(): void {
    this.modalService.openModal({
      title: 'Conferma uscita',
      description: 'Sei sicuro di voler uscire?',
      primaryButtonText: 'Esci',
      secondaryButtonText: 'Rimani',
      onConfirm: () => {
        this.authService.logout();
      }
    });
  }

  // Calcola l'altezza dell'header e la espone come CSS variable globale,
  // così il MenuGlobal può allineare il proprio `top` in modo dinamico
  // (fino a 1024px di larghezza schermo).
  ngAfterViewInit(): void {
    this.updateHeaderHeight();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateHeaderHeight();
  }

  private updateHeaderHeight(): void {
    try {
      const headerEl = this.hostElement.nativeElement.querySelector('header');
      const height = headerEl?.offsetHeight ?? 0;

      if (height > 0 && typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    } catch {
      // In caso di errori di accesso al DOM (es. ambienti non browser),
      // semplicemente non aggiorniamo la variabile CSS.
    }
  }

  // Apertura/chiusura menu sinistro (hamburger) presente in MenuGlobal
  toggleLeftMenu(): void {
    const sidebar = document.querySelector('#sidebar') as HTMLElement | null;
    if (!sidebar) {
      return;
    }

    sidebar.classList.toggle('open');
    const isNowOpen = sidebar.classList.contains('open');
    this.isLeftMenuOpen = isNowOpen;

    // Se il menu è appena stato aperto, chiudi il dropdown ditta (se aperto)
    if (isNowOpen) {
      // Se il dropdown è effettivamente aperto lo chiudiamo "da fuori":
      // in questo caso l'evento expandedChange(false) che ne risulta non deve
      // causare la chiusura del menu che abbiamo appena aperto.
      if (this.dittaDropdown?.isExpanded) {
        this.suppressDropdownCloseMenu = true;
        this.dittaDropdown.closeFromOutside();
      }
      else {
        // Nessuna chiusura indiretta del dropdown -> nessun evento da sopprimere
        this.suppressDropdownCloseMenu = false;
      }
    }
  }

  /**
   * Chiusura forzata del menu sinistro (usata, ad esempio,
   * quando il dropdown in header viene aperto/chiuso).
   */
  private closeLeftMenuIfOpen(): void {
    const sidebar = document.querySelector('#sidebar') as HTMLElement | null;
    if (sidebar?.classList.contains('open')) {
      sidebar.classList.remove('open');
    }
    this.isLeftMenuOpen = false;
  }

  /**
   * Gestisce l'evento di apertura/chiusura del dropdown ditta
   * presente SOLO in questo header.
   * Qualsiasi cambiamento di stato del dropdown comporta la
   * chiusura del menu laterale, se presente.
   */
  onDropdownExpandedChange(_: boolean): void {
    // Se la chiusura del dropdown è stata innescata
    // dall'apertura del menu (closeFromOutside), ignoriamo
    // questo singolo evento per non richiudere subito il menu.
    if (this.suppressDropdownCloseMenu) {
      this.suppressDropdownCloseMenu = false;
      return;
    }
    this.closeLeftMenuIfOpen();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    // Chiudi eventuale menu sinistro aperto
    const sidebar = document.querySelector('#sidebar') as HTMLElement | null;
    if (sidebar?.classList.contains('open')) {
      sidebar.classList.remove('open');
      this.isLeftMenuOpen = false;
    }
  }
}
